import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Button,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Text,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { SearchIcon, InfoIcon } from "@chakra-ui/icons";
import { createProspectList } from "../../services/api";

const NovaListaModal = ({ isOpen, onClose, onListCreated }) => {
  const [formData, setFormData] = useState({
    name: "",
    search_query: "",
    num_results: 10,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError(null);
  };

  const handleSubmit = async () => {
    if (!formData.name.trim() || !formData.search_query.trim()) {
      setError({
        type: "validation",
        message: "Nome e consulta de busca são obrigatórios"
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await createProspectList(formData);

      if (result && result.list) {
        onListCreated(result.list);
        setFormData({
          name: "",
          search_query: "",
          num_results: 10,
        });
      } else if (result && result.error) {
        setError({
          type: "api_error",
          message: typeof result.error === 'string'
            ? result.error
            : "Erro de validação nos dados fornecidos"
        });
      } else {
        setError({
          type: "api_error",
          message: "Erro ao criar lista de prospecção"
        });
      }
    } catch (error) {
      setError({
        type: "api_error",
        message: "Erro ao conectar com o servidor"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        name: "",
        search_query: "",
        num_results: 10,
      });
      setError(null);
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={SearchIcon} color="blue.500" />
            <Text>Nova Lista de Prospecção</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton isDisabled={loading} />

        <ModalBody>
          <VStack spacing={5} align="stretch">
            {/* Informação sobre o processo */}
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <AlertTitle fontSize="sm">Como funciona?</AlertTitle>
                <AlertDescription fontSize="sm">
                  Usamos a API Google Serper para encontrar lugares relacionados à sua busca.
                  Os resultados serão organizados em uma lista para você prospectar.
                </AlertDescription>
              </VStack>
            </Alert>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <AlertTitle fontSize="sm">
                    {error.type === "validation" ? "Erro de Validação" : "Erro"}
                  </AlertTitle>
                  <AlertDescription fontSize="sm">
                    {error.message}
                  </AlertDescription>
                </VStack>
              </Alert>
            )}

            <FormControl isRequired>
              <FormLabel>Nome da Lista</FormLabel>
              <Input
                placeholder="Ex: Oficinas Mecânicas São Paulo"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                isDisabled={loading}
                maxLength={255}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {formData.name.length}/255 caracteres
              </Text>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Consulta de Busca</FormLabel>
              <Textarea
                placeholder="Ex: oficinas mecânicas centro são paulo"
                value={formData.search_query}
                onChange={(e) => handleInputChange("search_query", e.target.value)}
                isDisabled={loading}
                maxLength={255}
                rows={3}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                Seja específico para obter melhores resultados. {formData.search_query.length}/255 caracteres
              </Text>
            </FormControl>

            <FormControl>
              <FormLabel>Número de Resultados</FormLabel>
              <NumberInput
                value={formData.num_results}
                onChange={(_, value) => handleInputChange("num_results", value || 10)}
                min={1}
                max={100}
                isDisabled={loading}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Quantidade de lugares para buscar (entre 1 e 100)
              </Text>
            </FormControl>

            <Alert status="warning" borderRadius="md" size="sm">
              <AlertIcon />
              <VStack align="start" spacing={1}>
                <AlertTitle fontSize="xs">
                  <Icon as={InfoIcon} mr={1} />
                  Importante
                </AlertTitle>
                <AlertDescription fontSize="xs">
                  Você só pode criar uma nova lista após completar todas as listas pendentes.
                  Isso garante foco e qualidade na prospecção.
                </AlertDescription>
              </VStack>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={3}>
            <Button
              variant="ghost"
              onClick={handleClose}
              isDisabled={loading}
            >
              Cancelar
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSubmit}
              isLoading={loading}
              loadingText="Criando..."
              leftIcon={<SearchIcon />}
            >
              Criar Lista
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NovaListaModal;